import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl-next';
import type { PostQuerySupplierArgs } from '../../product-router/product/result-provider/result-provider-types';
import type { ResultRendererArgs, SearchItems, SearchResult } from '../../product-router/product/result-types';
import { useTypedAggregatorClient } from '../../aggregator-client-context/aggregator-client-context';
import { CrossProductScope } from './types';
import { messages } from './messages';
import { defaultResultRenderer } from '../../search-result/default-result-renderer';
import { fetchRecentActivities } from './recent-activities';
import { useSearchSessionId } from '../../../common/search-session-provider/search-session-provider';
import { useCurrentProductHasAppliedFilters } from '../../filters/filters-helper-functions';
import { filterFirstSectionItems } from '../../product-router/product/result-provider/faster-search-helpers';

export const CROSS_PRODUCT_PRODUCT_ID = 'rovo-cross-product';

const resultRenderer = (args: ResultRendererArgs<SearchResult>) =>
	defaultResultRenderer({
		...args,
		isCollapsed: true,
	});

export const useCrossProductQuerySupplier = () => {
	const intl = useIntl();
	const aggregatorClient = useTypedAggregatorClient();
	const cloudId = aggregatorClient?.getAggregatorConfig().cloudId;
	const searchSessionId = useSearchSessionId();
	const [results, setResults] = useState<SearchResult[]>([]);
	const [isQueryResolved, setIsQueryResolved] = useState(false);
	const hasFilters = useCurrentProductHasAppliedFilters();

	const preQuerySupplier = useCallback(async () => {
		let currentResults = results;

		if (!isQueryResolved) {
			const response = await fetchRecentActivities(cloudId || '', searchSessionId || '');
			currentResults = response.filter((result) => result !== undefined) as SearchResult[];
			setResults(currentResults);
			setIsQueryResolved(true);
		}

		return {
			size: currentResults.length,
			sections: [
				{
					id: CrossProductScope.RECENTS,
					title: intl.formatMessage(messages.cross_product_recents_section_heading),
					searchResults: currentResults,
					resultRenderer,
				},
			],
		};
	}, [cloudId, intl, isQueryResolved, results, searchSessionId]);

	const postQuerySupplier = useCallback(
		async (args: PostQuerySupplierArgs): Promise<SearchItems> => {
			let currentResults = results;

			if (!isQueryResolved) {
				const response = await fetchRecentActivities(cloudId || '', searchSessionId || '');
				currentResults = response.filter((result) => result !== undefined) as SearchResult[];
				setResults(currentResults);
				setIsQueryResolved(true);
			}

			const searchResults = {
				size: currentResults.length,
				sections: [
					{
						id: CrossProductScope.RESULTS,
						title: intl.formatMessage(messages.cross_product_results_section_heading),
						searchResults: currentResults,
						resultRenderer,
					},
				],
			};

			return filterFirstSectionItems(searchResults, args.query, hasFilters ? 0 : 3);
		},
		[cloudId, intl, isQueryResolved, results, searchSessionId, hasFilters],
	);

	return {
		preQuerySupplier,
		postQuerySupplier,
	};
};
